import { Link } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Pagination from '@/components/ui/Pagination'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import RecentArticles from '@/components/sections/RecentArticles'
import Newsletter from '@/components/sections/Newsletter'
import { useFilterProgrammes, useSearchProgrammes } from '@/lib/api/hooks/programme'
import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import FullPageSpinner from '@/components/ui/FullPageSpinner'
import { parseHtmlString } from '@/utils/helpers/html'

import Search from '@/components/ui/Search'

import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'

import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'

import FILTER_SECTIONS from './FilterModule/FILTER_SECTIONS'

import FilterModule from '@/components/ui/FilterModule'

interface ProgramItem {
  id: string
  title: string
  description: string
  image: string
  category: string
  href: string
}


// Parse HTML content to extract text for description
const parseHTMLContent = (html: string): string => {
  if (!html) return ''
  // Create a temporary DOM element to parse HTML (client-side only)
  if (typeof document !== 'undefined') {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    return tempDiv.textContent || tempDiv.innerText || ''
  } else {
    // Fallback: strip HTML tags using regex (for SSR)
    return html.replace(/<[^>]*>/g, '').trim()
  }
}

const Programs = () => {
  const { data, isFetching, isError, refetch } = useAllWebPages();
  const pageData = data?.data && data?.data.find((page) =>
    page.codeName?.toLowerCase() === 'programmes' ||
    page.websitePageName?.toLowerCase() === 'programmes'
  )?.content || null;

  // ── URL-driven search / filter state ──────────────────────────────────────
  const {
    mode,
    searchTerm,
    filterState,
    page,
    size,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    apiFilters,
    getActiveFilterCount
  } = useSearchFilterParams(FILTER_SECTIONS)


  // Search API
  const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchProgrammes(
    searchTerm,
    { page, size }
  )

  // Default API
  const { data: programmesData, isFetching: isProgrammesFetching, isError: isProgrammesError, refetch: refetchProgrammes } = useFilterProgrammes(
    { page, size },
    apiFilters
  )

  // Select appropriate data source
  const selectedData = mode === 'search' ? searchData : programmesData
  const isSearchFilterFetching = mode === 'search' ? isSearchFetching : isProgrammesFetching
  const searchFilterError = mode === 'search' ? isSearchError : isProgrammesError
  const refetchData = mode === 'search' ? refetchSearch : refetchProgrammes
    const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS)
      const emptyMessage = mode === 'search' ? 'No program found for this search query.' : mode === "filter" ? 'No program found for this filter.' : 'No program found.'

  // Extract data from API
  const apiProgrammes = selectedData?.data?.data || []
  const totalPages = selectedData?.data?.totalPage || 1

  // Transform API data to component format
  const programs: ProgramItem[] = apiProgrammes.map((prog) => {
    // Extract text from HTML content
    const textContent = parseHTMLContent(prog.content || '')
    const description = textContent
      ? textContent.substring(0, 200) + (textContent.length > 200 ? '...' : '')
      : ''

    return {
      id: prog.programmeId,
      title: prog.title,
      description,
      image: prog.coverImageUrl,
      category: prog.articleTag || 'General',
      href: `/programmes/programs/${prog.programmeId}`
    }
  })


  // No client-side filtering needed - API handles search
  const filteredPrograms = programs


  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Programmes', href: '/programmes/programs', active: true }
  ]


  if (isFetching) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button onClick={() => refetch()} className="px-4 py-2 bg-wine-100 hover:bg-wine-200 text-white rounded">Retry</button>
    </div>;
  }

  if (!pageData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p></p>
    </div>;
  }

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <div className=" mb-5 ">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Hero Image */}
      <div className="mb-16">
        <img
          src={pageData?.PROGRAMME_BANNER?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1756135981/b467cbb129e3f72467993c785f41b2ffab8d0f62_1_cqe6qb.png"}
          alt="African Space Agency Building"
          className="w-full h-64 sm:h-80 md:h-96 lg:h-[480px] object-cover rounded"
        />
      </div>

      {/* Main Content */}
      <div className="mb-16 space-y-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900 leading-tight lg:leading-12">
          {pageData?.PROGRAMMES?.title ?? "Our Programmes and Initiatives"}
        </h1>
        <div className="max-w-4xl">
          <p className="" dangerouslySetInnerHTML={parseHtmlString(pageData?.PROGRAMMES?.description ?? "")}>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-14">
        {/* Sidebar */}
        <div className="lg:col-span-1 order-2 lg:order-1">
          <div className="space-y-10">

            {/* Related Articles */}
            <RecentArticles />

            {/* Newsletter */}
            <Newsletter />
          </div>
        </div>

        {/* Main Content - Programmes */}
        <div className="lg:col-span-3 order-1 lg:order-2">
          <div className="space-y-8">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-normal font-pt-serif text-gray-900">
              PROGRAMMES
            </h2>

                           {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search programmes..."
              />
              <div style={{display: "none"}}>
                <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
              </div>
               
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

            {/* Programs Grid */}
            <div className="space-y-6">
              {
                 isSearchFilterFetching ? (
                  <div className="py-20">
                    <LoadingSpinner size="lg" message="Loading programs..." />
                  </div>
                ) : searchFilterError ? (
                  <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
                    <p>Error loading programs.</p>
                    <button onClick={() => refetchData()} className="px-4 py-2 bg-wine-100 hover:bg-wine-200 text-white rounded">Retry</button>
                  </div>
                ) : programs.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-lg">{emptyMessage}</p>
                  </div>
                ) :
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredPrograms.map((program) => (
                  <Link
                    key={program.id}
                    to={program.href}
                    className="group block"
                  >
                    <article className="space-y-4">
                      <div className="w-full h-72">
                        <img
                          src={program.image}
                          alt={program.title}
                          className="w-full h-full object-cover rounded group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="space-y-4">
                        <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7 group-hover:text-wine-100 transition-colors">
                          {program.title}
                        </h3>
                        <p className="text-base font-normal font-satoshi text-gray-700 leading-relaxed line-clamp-3">
                          <span dangerouslySetInnerHTML={parseHtmlString(program.description)} />
                        </p>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
}

                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Programs